import { useState } from "react";
import { BarChart3, Save, Eye, Code } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface AnalyticsConfig {
  yandex_metrika_id: string;
  vk_pixel_id: string;
  vk_access_token: string;
  enable_tracking: boolean;
  custom_head_code: string;
}

const DEFAULT_CONFIG: AnalyticsConfig = {
  yandex_metrika_id: "",
  vk_pixel_id: "",
  vk_access_token: "",
  enable_tracking: false,
  custom_head_code: "",
};

export default function Analytics() {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<AnalyticsConfig | null>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ["analytics_config"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("*")
        .eq("key", "analytics_config")
        .single();
      if (error && error.code !== "PGRST116") throw error;
      return (data?.value as unknown as AnalyticsConfig) || DEFAULT_CONFIG;
    },
  });

  const current = formData ?? config ?? DEFAULT_CONFIG;

  const handleChange = <K extends keyof AnalyticsConfig>(key: K, value: AnalyticsConfig[K]) => {
    setFormData((prev) => ({ ...(prev ?? config ?? DEFAULT_CONFIG), [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = formData ?? config ?? DEFAULT_CONFIG;
      const { error } = await supabase
        .from("settings")
        .upsert(
          { key: "analytics_config", value: toSave as unknown as string, description: "Аналитика и пиксели" },
          { onConflict: "key" }
        );
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["analytics_config"] });
      setFormData(null);
      toast.success("Настройки аналитики сохранены");
    } catch {
      toast.error("Ошибка при сохранении");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Аналитика и пиксели" description="Яндекс.Метрика, VK Pixel, пользовательские скрипты">
      <div className="space-y-6">
        {/* Enable tracking */}
        <Card className="bg-card border-border border-green-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-500" />
              Отслеживание
            </CardTitle>
            <CardDescription>Глобальный переключатель всех пикселей и скриптов</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>Включить трекинг</Label>
                <p className="text-xs text-muted-foreground">
                  Если выключено — никакие скрипты аналитики не подгружаются
                </p>
              </div>
              <Switch
                checked={current.enable_tracking}
                onCheckedChange={(v) => handleChange("enable_tracking", v)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Yandex Metrika */}
        <Card className="bg-card border-border border-yellow-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-yellow-500" />
              Яндекс.Метрика
            </CardTitle>
            <CardDescription>Счётчик Яндекс.Метрики для отслеживания визитов и целей</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>ID счётчика</Label>
              <Input
                value={current.yandex_metrika_id}
                onChange={(e) => handleChange("yandex_metrika_id", e.target.value)}
                placeholder="12345678"
              />
              <p className="text-xs text-muted-foreground">
                Числовой ID из Яндекс.Метрики → Настройки → Код счётчика
              </p>
            </div>
          </CardContent>
        </Card>

        {/* VK Pixel */}
        <Card className="bg-card border-border border-blue-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              VK Pixel
            </CardTitle>
            <CardDescription>Пиксель VK Ads для ретаргетинга и аналитики рекламы</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Pixel ID</Label>
              <Input
                value={current.vk_pixel_id}
                onChange={(e) => handleChange("vk_pixel_id", e.target.value)}
                placeholder="VK-RTRG-123456-xxxxx"
              />
              <p className="text-xs text-muted-foreground">
                ID пикселя из VK Ads → Аудитории → Пиксели
              </p>
            </div>
            <div className="space-y-2">
              <Label>Access Token (для server-side событий)</Label>
              <Input
                type="password"
                value={current.vk_access_token}
                onChange={(e) => handleChange("vk_access_token", e.target.value)}
                placeholder="vk1.a.xxx..."
              />
              <p className="text-xs text-muted-foreground">
                Токен с правом ads для отправки серверных событий purchase через VK Conversions API
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Custom Head Code */}
        <Card className="bg-card border-border border-purple-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-purple-500" />
              Произвольный код
            </CardTitle>
            <CardDescription>HTML/JS-код, который будет вставлен перед &lt;/head&gt;</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Custom Head Code</Label>
              <Textarea
                value={current.custom_head_code}
                onChange={(e) => handleChange("custom_head_code", e.target.value)}
                placeholder="<script>...</script>"
                rows={6}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                Любой HTML/JS для вставки: Google Tag Manager, Facebook Pixel, и т.д.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Events info */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>📌 Автоматические события</CardTitle>
            <CardDescription>Эти события отправляются автоматически при включённом трекинге</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3 p-2 rounded bg-muted/50">
                <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">PageView</span>
                <span className="text-muted-foreground">Просмотр страницы — Метрика + VK Pixel</span>
              </div>
              <div className="flex items-start gap-3 p-2 rounded bg-muted/50">
                <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">start_hit_creation</span>
                <span className="text-muted-foreground">Клик «Создать хит» — с UTM-параметрами</span>
              </div>
              <div className="flex items-start gap-3 p-2 rounded bg-muted/50">
                <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">demo_listen</span>
                <span className="text-muted-foreground">Прослушивание демо (server-side из бота)</span>
              </div>
              <div className="flex items-start gap-3 p-2 rounded bg-muted/50">
                <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">initiate_checkout</span>
                <span className="text-muted-foreground">Нажатие кнопки оплаты (server-side из бота)</span>
              </div>
              <div className="flex items-start gap-3 p-2 rounded bg-muted/50">
                <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">purchase</span>
                <span className="text-muted-foreground">Успешная оплата — server-side + VK Conversions API</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || !formData}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Сохранение..." : "Сохранить"}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
